import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { normalizePaginatedListResponse } from '../../services/paginatedList';
import { useManagementCompanyLogoBlobUrl } from '../wrh/reports/useManagementCompanyLogo';
import { moduleConfig } from './config';
import { PayslipDocument } from './reports/PayslipDocument';
import { ReportShell } from './reports/ReportShell';
import { useMgaPayslip } from './useData';

type ManagementCompanyRow = {
  id: number;
  name: string;
  isActive?: boolean;
  logoStorageKey?: string | null;
};

const Payslip: React.FC = () => {
  const { colors } = useTheme();
  const { id, lineId } = useParams<{ id: string; lineId: string }>();
  const periodId = id ? parseInt(id, 10) : NaN;
  const lid = lineId ? parseInt(lineId, 10) : NaN;
  const { data, isLoading } = useMgaPayslip(
    Number.isInteger(periodId) ? periodId : 0,
    Number.isInteger(lid) ? lid : 0,
  );
  const { data: companiesRaw } = useSWR('/management-companies?page=1&limit=50', fetcher, {
    revalidateOnFocus: false,
  });

  const managementCompany = useMemo(() => {
    const fromApi = data?.managementCompany;
    if (fromApi?.id && fromApi.logoStorageKey) {
      return {
        id: fromApi.id,
        name: fromApi.name,
        logoStorageKey: fromApi.logoStorageKey,
      };
    }
    const list = normalizePaginatedListResponse<ManagementCompanyRow>(companiesRaw, 1, 50).data;
    const active = list.filter((c) => c.isActive !== false);
    const withLogo = active.find((c) => c.logoStorageKey) ?? list.find((c) => c.logoStorageKey);
    if (withLogo?.logoStorageKey) {
      return {
        id: withLogo.id,
        name: fromApi?.name || withLogo.name,
        logoStorageKey: withLogo.logoStorageKey,
      };
    }
    return fromApi?.id
      ? { id: fromApi.id, name: fromApi.name, logoStorageKey: fromApi.logoStorageKey ?? null }
      : null;
  }, [data?.managementCompany, companiesRaw]);

  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    managementCompany?.id,
    managementCompany?.logoStorageKey,
  );

  if (!Number.isInteger(periodId) || !Number.isInteger(lid)) {
    return (
      <div className="p-8 text-center">
        <Link to={moduleConfig.module.route.list} className="text-blue-600 underline">
          Back to MGA
        </Link>
      </div>
    );
  }

  if (!isLoading && !data) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>Payslip not found</h1>
        <Link to={`/mga/${periodId}`} className="mt-4 inline-block text-sm underline">
          Back to period
        </Link>
      </div>
    );
  }

  const period = data?.period;
  const line = data?.line;
  const subtitle = line
    ? [line.crewName, line.rankName, period?.currency || 'USD'].filter(Boolean).join(' · ')
    : undefined;

  return (
    <ReportShell
      periodId={periodId}
      title="Monthly pay account"
      subtitle={subtitle}
      loading={isLoading || !data}
    >
      {period && line ? (
        <PayslipDocument
          period={period}
          line={line}
          companyLogoSrc={companyLogoSrc}
          companyLogoAlt={managementCompany?.name || 'Management company logo'}
        />
      ) : null}
    </ReportShell>
  );
};

export default Payslip;
